import React, { useState } from 'react';
import './MentorOnboardingTour.css';

const SLIDES = [
  {
    icon: '🎓',
    title: (name) => `Welcome to DevBrains, ${name}! You're now a Mentor.`,
    content: (
      <p className="mot-desc">
        DevBrains connects you with motivated learners worldwide. Share your expertise
        through 1:1 sessions and earn on your own schedule — completely on your terms.
      </p>
    ),
  },
  {
    icon: '⚙️',
    title: () => 'How It Works',
    content: (
      <ol className="mot-list">
        {[
          'Learners discover your profile and browse your expertise',
          'They book from your available time slots',
          'You confirm and conduct the live 1:1 session',
          'Payment is deposited to your wallet within 2 business days',
          'Learners leave reviews that build your reputation on the platform',
        ].map((text, i) => (
          <li key={i} className="mot-list-item">
            <span className="mot-list-num">{i + 1}</span>
            <span>{text}</span>
          </li>
        ))}
      </ol>
    ),
  },
  {
    icon: '👤',
    title: () => 'Set Up Your Profile',
    content: (
      <>
        <p className="mot-desc">A complete profile gets you discovered faster. Make sure to add:</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            'Professional headline',
            'Bio — your story and expertise',
            'Skills and technologies',
            'Session pricing',
          ].map((item) => (
            <div key={item} className="mot-checklist-item">{item}</div>
          ))}
        </div>
      </>
    ),
  },
  {
    icon: '🗓️',
    title: () => 'Configure Your Availability',
    content: (
      <>
        <p className="mot-desc">
          Set your available time slots so learners can book you at the right times.
        </p>
        <ul className="mot-list">
          {[
            'Go to Dashboard → Availability',
            'Set recurring weekly slots for each day',
            'Block dates when you are unavailable',
            'Choose your session duration (30 or 60 minutes)',
          ].map((item, i) => (
            <li key={i} className="mot-list-item">
              <span className="mot-list-bullet" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
  {
    icon: '✏️',
    title: () => 'Manage Your Profile',
    content: (
      <>
        <p className="mot-desc">Keep your profile fresh to grow your ranking and bookings.</p>
        <ul className="mot-list">
          {[
            'Update your bio and headline regularly',
            'Respond to enquiries quickly',
            'Ask mentees to leave reviews after sessions',
            'Share your mentor profile link on social media',
          ].map((item, i) => (
            <li key={i} className="mot-list-item">
              <span className="mot-list-bullet" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </>
    ),
  },
];

/**
 * MentorOnboardingTour
 * Full-screen overlay modal with 5 slides for newly approved mentors.
 *
 * Props:
 *   open      — boolean controlling visibility
 *   onClose   — callback when tour is closed or finished
 *   userName  — mentor's first name for the welcome slide
 */
export default function MentorOnboardingTour({ open, onClose, userName = 'Mentor' }) {
  const [step, setStep] = useState(0);

  if (!open) return null;

  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;
  const isFirst = step === 0;
  const progress = ((step + 1) / SLIDES.length) * 100;

  const goNext = () => {
    if (isLast) {
      onClose();
    } else {
      setStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (!isFirst) setStep((s) => s - 1);
  };

  return (
    <div className="mot-overlay" role="dialog" aria-modal="true" aria-label="Mentor onboarding tour">
      <div className="mot-box">

        {/* Progress bar */}
        <div className="mot-progress-bar">
          <div className="mot-progress-fill" style={{ width: `${progress}%` }} />
        </div>

        {/* Step indicator */}
        <div className="mot-step-label">
          Step {step + 1} of {SLIDES.length}
        </div>

        {/* Slide content */}
        <div className="mot-body" key={step}>
          <div className="mot-icon">{slide.icon}</div>
          <h2 className="mot-title">
            {typeof slide.title === 'function' ? slide.title(userName) : slide.title}
          </h2>
          {slide.content}
        </div>

        {/* Progress dots */}
        <div className="mot-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`mot-dot${i === step ? ' mot-dot--active' : ''}`}
              onClick={() => setStep(i)}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mot-footer">
          <div className="mot-footer-main">
            {!isFirst && (
              <button className="mot-back-btn" onClick={goBack}>
                Back
              </button>
            )}
            {isLast ? (
              <button className="mot-finish-btn" onClick={onClose}>
                Go to Dashboard
              </button>
            ) : (
              <button className="mot-next-btn" onClick={goNext}>
                Next
              </button>
            )}
          </div>
          {!isLast && (
            <button className="mot-skip-btn" onClick={onClose}>
              Skip Tour
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
